import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { LocationFormData, LocationsQueryParams } from '@rideiq/locations/types';

import { createLocation, getLocationById, updateLocation } from '../api/services/Locations';
import { ApiResponse } from '../api/utils/responseTransform';
import { useLocationsQuery } from '../locations/hooks/useLocationsQuery';
import { usePlacesQuery } from '../locations/hooks/usePlacesQuery';
import { useToastStore } from '../shared/stores/toastStore';
import filterObjectByKeys from '../utils/helpers/filterObjectByKeys';

export type GroupedOption = {
  group: string;
  label: string;
  placeId: string;
  dropoffProcedureInstructions: string;
  pickupProcedureInstructions: string;
  passengerCount: string;
  id?: string;
  name?: string;
  address?: string;
  streetAddressTwo?: string;
  calendar?: string;
  calendarName?: string;
  latLng?: string;
  preciseLatLng?: string;
  locationType?: string;
};

interface UseLocationOptionsHook {
  debounceHandleQuery: (query: string, options?: { force?: boolean }) => void;
  locations: GroupedOption[];
  locationOptions: GroupedOption[];
  saveNewLocation: (data: LocationFormData) => Promise<AccountLocation | null>;
  updateLocation: (data: Partial<LocationFormData>) => Promise<ApiResponse<{ accountLocation: AccountLocation }>>;
  addMissingLocations: (ids: string[]) => Promise<void>;
  setQueryParams: Dispatch<SetStateAction<LocationsQueryParams>>;
}

export const CREATE_NEW_LOCATION_GROUP = 'Create a new location:';
const SAVED_LOCATION_GROUP = 'Saved locations:';

export const useLocationOptions = (locationType?: string | undefined): UseLocationOptionsHook => {
  const { addToast } = useToastStore();
  const {
    debounceHandleQuery,
    locations: newLocations,
    queryParams,
    setQueryParams,
  } = useLocationsQuery(undefined, {
    locationType,
  });
  const { places } = usePlacesQuery(queryParams.query);
  const [locations, setLocations] = useState<GroupedOption[]>([]);

  useEffect(() => {
    if (newLocations.length) {
      const formattedLocations = formatLocations(newLocations);

      // append the newly searched locations to locations, but filter any repeating locations
      setLocations((previous) => [
        ...new Map([...previous, ...formattedLocations].map((location) => [location.id, location])).values(),
      ]);
    }
  }, [newLocations]);

  const saveNewLocation = async (data) => {
    const response = await createLocation(data);

    if (response?.type === 'success') {
      const newLocationResponse = await getLocationById(response.data.id);

      if (newLocationResponse?.type === 'success') {
        const newLocation = formatLocations([newLocationResponse?.data.accountLocation]);

        setLocations([...locations, ...newLocation]);

        return newLocationResponse?.data.accountLocation;
      }
    } else {
      response.error.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
    }

    return null;
  };

  const updateLocationDetails = async (data) => {
    const response = await updateLocation(data.id, filterObjectByKeys(data, locationAttributes));

    if (response?.type !== 'error') {
      const indexOfLocation = locations.findIndex((location) => location.id == data.id);
      const updatedLocation = formatLocations([response.data.accountLocation]);

      setLocations([
        ...locations.slice(0, indexOfLocation),
        ...updatedLocation,
        ...locations.slice(indexOfLocation + 1),
      ]);
    }

    return response;
  };

  const addMissingLocations = async (ids) => {
    const locationPromises = await Promise.all(ids.map((id) => getLocationById(id)));
    const formattedLocations = formatLocations(locationPromises.map((response) => response?.data?.accountLocation));

    setLocations([...locations, ...formattedLocations]);
  };

  return {
    debounceHandleQuery,
    locations: locations,
    locationOptions: [...formatLocations(newLocations), ...(queryParams.query ? formatPlaces(places) : [])],
    saveNewLocation,
    addMissingLocations,
    updateLocation: updateLocationDetails,
    setQueryParams,
  };
};

export function formatLocations(locations: AccountLocation[]) {
  return locations
    .filter((loc) => loc !== undefined) // Ensure each location object is defined
    .map((loc) => ({
      group: SAVED_LOCATION_GROUP,
      label: loc.name ? `${loc.name}, ${loc.address}` : loc.address,
      placeId: loc.placeId,
      pickupProcedureInstructions: loc.pickupProcedure?.instructions,
      dropoffProcedureInstructions: loc.dropoffProcedure?.instructions,
      passengerCount: `${loc.passengerCount || 0}`,
      name: loc.name,
      address: loc.address,
      streetAddressTwo: loc.streetAddressTwo || '',
      calendar: `${loc.calendar?.slug || ''}`,
      calendarName: `${loc.calendar?.name || ''}`,
      id: loc.id.toString(),
      latLng: `${loc.lat},${loc.lng}`,
      preciseLatLng: loc.preciseLocation?.lat ? `${loc.preciseLocation.lat},${loc.preciseLocation.lng}` : undefined,
      locationType: loc.locationType || 'other',
    }));
}
function formatPlaces(places: Place[]) {
  return places.map((loc) => ({
    group: CREATE_NEW_LOCATION_GROUP,
    label: loc.description,
    placeId: loc.placeId,
    pickupProcedureInstructions: '',
    dropoffProcedureInstructions: '',
    passengerCount: '0',
    name: loc?.structuredFormatting?.mainText,
    address: loc.description.replace(', USA', ''),
    streetAddressTwo: '',
  }));
}

const locationAttributes = [
  'calendarSlug',
  'dropoffProcedureInstructions',
  'locationType',
  'name',
  'placeId',
  'pickupProcedureInstructions',
  'preciseLocation',
  'streetAddressTwo',
  'tripSlug',
  'clearAllInstructions',
];
